/**
 * Created by macuser on 6/22/16.
 */

/**
 * The `QueryModel` class represents a query expression. Currently, it only supports `and` and `or` operations.
 * @module app/models/map/query/QueryModel
 */
import _ from "underscore";
import FieldTypes from "core/models/FieldTypes";
import Field from "core/models/Field";
import mapContext from "app/views/map/MapContext";
import * as Terraformer from "terraformer-arcgis-parser";
import * as TerraformerPrimitive from "terraformer";

/**
 * Creates a new query model.
 * @constructor
 */
function QueryModel() {
  this.layout = mapContext.getConfiguration();
  this._updateSelectedLayersAndFields();
  this.reset();
}

QueryModel.prototype = {
  /**
   * Adds a clause to the current "and" expression.
   * @param {core/models/Field} [field] The field associated with the clause. If this is null, all fields are searched for the given value.
   * @param {String} value The desired value for the field.
   * @returns {QueryModel}
   */
  and: function (field, value) {
    this.expression[this.expression.length - 1].push({
      field: field,
      value: value,
    });

    return this;
  },

  /**
   * Iterates through searchLayers, retuning fields that can queried.
   * @returns {Array} Fields
   */
  _getQueryableFields: function () {
    return QueryModel.getQueryableFields({ layers: this.searchLayers });
  },

  /**
   * For multiple layers, returns fields that share similar labels.
   * @returns {Object} that describes what fields are shared between layers. e.g.
   * { label: [layerId1, layerId2], label2: [layerId3, layerId4] };
   * */
  _getLikeLabeledFields: function () {
    return QueryModel.getLikeLabeledFields(this.searchLayers);
  },

  /**
   * Updates searchLayers.
   * Updates fields that can be queried.
   * Updates similarly labeledFields between layers.
   * Updates whether query model contains all internal or external layers.
   */
  _updateSelectedLayersAndFields: function () {
    this.searchLayers = this.layout.getSelectedLayers();
    this.searchLayersBackup = this.searchLayers;
    this.fields = this._getQueryableFields();
    this.likeLabeledFields = this._getLikeLabeledFields();
    this.allExternal = this.layout.allExternal();
    this.allInternal = this.layout.allInternal();
  },

  /**
   * Created for Unit test cases.
   * Unit test case sets this.terraformer.
   */
  _getTerraformer: function () {
    if (this.terraformer) return this.terraformer;
    else return Terraformer;
  },

  /**
   * Created for Unit test cases.
   * Unit test case sets value for this.terraformerEx.
   */
  _getTerraformerEx: function () {
    if (this.terraformerEx) return this.terraformerEx;
    else return Terraformer;
  },

  /**
   * Created for Unit test cases.
   * Unit test case sets value for this.terraformerPrimitive.
   */
  _getTerraformerPrimitive: function () {
    if (this.terraformerPrimitive) return this.terraformerPrimitive;
    else return TerraformerPrimitive;
  },

  /**
   * Filters Internal or External layers from the selected layers.
   * @param {Boolean} internalOnly True filters internal service layers and False filters external service layers.
   * @param {Array} layers Layers that needs to be filtered.
   * @returns {Array} Layers
   */
  filterSelectedLayers: function (internalOnly, layers) {
    var selectedLayers = _.filter(layers, function (layer) {
      //This is not an ideal way to find whether the layer is internal or external.
      //However currently don't see an property that represent the internal or exterl service's layer
      if (layer.layer != null && layer.layer != undefined) {
        if (internalOnly)
          return layer.layer.url == null || layer.layer.url == undefined;
        //For internal layer url is null
        else return layer.layer.url != null && layer.layer.url != undefined; //For external layer url is not null
      }
      return false;
    });

    return selectedLayers;
  },

  /**
   * Filters Internal or External layers from the selected layers.
   * @param {Boolean} internalOnly True filters internal service layers and False filters external service layers.
   * @returns {Array} Layers
   */
  getSelectedLayers: function (internalOnly) {
    this.searchLayers = this.filterSelectedLayers(
      internalOnly,
      this.searchLayersBackup
    );

    return this.searchLayers;
  },

  /**
   * Creates Fields to be used for search input
   * Iterates through fields associated with selectedLayers, creating search fields indexed by Label
   * @returns {Array} Fields
   */
  getSearchFields: function () {
    this._updateSelectedLayersAndFields();

    this.fieldsByKey = {};

    if (this.allInternal || this.allExternal) {
      return _.map(
        this.fields,
        function (field) {
          var fieldLabel = field.get("label");
          var searchField = new Field(_.clone(field.attributes));

          this.fieldsByKey[fieldLabel] = field;
          //For our search-input, ID and Name are irrelevant because they only reference the first field.
          //Use the label instead, which can be shared between layers.
          searchField.set("name", fieldLabel);
          searchField.set("id", fieldLabel);
          return searchField;
        },
        this
      );
    }
  },

  /**
   * Filters the selected layers base on Geometry selection.
   * @param {esri/geometry} geometry This geometry is used for searching.
   * @param {Array} selectedLayers These layers are searched.
   * @returns {Array} Filtered layers based on search.
   */
  runSpatialQuery: function (geometry, selectedLayers) {
    var models = [];
    var shapes = ["LineString", "Point"];
    var self = this;

    if (geometry) {
      // Method _getTerraformer is created for unit test case.
      // Unit test case sets the FakeTerraformer.
      var selectionShape = self._getTerraformer().parse(geometry.toJson());

      _.each(selectedLayers, function (layer) {
        var resultIds = [];

        _.each(layer.getGeometries(), function (featureGeometry) {
          var esriGeometry = featureGeometry.generateEsriGeometry();
          // Method _getTerraformerEx is created for unit test case.
          // Unit test case sets the FakeTerraformer.
          var searchableShape = self
            ._getTerraformerEx()
            .parse(esriGeometry.toJson());
          if (selectionShape.type == "MultiPoint") {
            selectionShape.forEach(function (point) {
              // Method _getTerraformerPrimitive is created for unit test case.
              // Unit test case sets the FakeTerraformer.
              var primitive = self._getTerraformerPrimitive();
              var transformedPoint = new primitive.Point(point[0], point[1]);
              if (
                searchableShape.within(transformedPoint) ||
                searchableShape.intersects(transformedPoint)
              )
                resultIds.push(featureGeometry.id);
            });
          } else if (searchableShape.within(selectionShape))
            resultIds.push(featureGeometry.id);
          else if (
            shapes.includes(selectionShape.type) &&
            selectionShape.intersects(searchableShape)
          )
            resultIds.push(featureGeometry.id);
        });

        var dataModels = _.map(layer.getGeometries(), layer.toData, layer);

        dataModels = _.filter(dataModels, function (dataModel) {
          return resultIds.includes(dataModel.id);
        });

        _.each(dataModels, function (dataModel) {
          dataModel.originalLayer = layer;
          models.push(dataModel);
        });
      });
    }

    return models;
  },

  /**
   * Completes an Internal Query by filtering the selected layers
   * @returns {Array} models matched by the selector
   */
  runInternalQuery: function () {
    var models = [];
    var selector = this.toFilterFunction();

    _.each(this.searchLayers, function (layer) {
      var potentialFields = layer.fields;
      var potentialFieldsHash = {};

      _.each(potentialFields, function (field) {
        potentialFieldsHash[field.id] = field.get("label");
      });

      var dataModels = _.map(layer.getGeometries(), layer.toData, layer);

      dataModels = _.filter(dataModels, selector);

      _.each(dataModels, function (dataModel) {
        dataModel.originalLayer = layer;
        models.push(dataModel);
      });
    });
    return models;
  },

  /**
   * Adds an additional "and" expression to the model with the given constraint.
   * @param {core/models/Field} [field] The field associated with the clause. If this is null, all fields are searched for the given value.
   * @param {String} value The desired value for the field.
   * @returns {QueryModel}
   */
  or: function (field, value) {
    this.expression.push([]);

    return this.and(field, value);
  },

  /**
   * Resets the query expression.
   */
  reset: function () {
    this.expression = [[]];
  },

  /**
   * Convert the current contents of this `QueryModel` to an expression which can be provided to an ESRI query.
   * @return {String} The serialized representation of this model.
   */
  toEsriQueryExpression: function (layer) {
    var self = this;

    function format(field, value) {
      var esriType = field.get("esriType");

      if (esriType == "esriFieldTypeString") {
        return "UPPER(" + field.get("name") + ") LIKE UPPER('%" + value + "%')";
      } else if (field.get("type") == FieldTypes.CheckBox) {
        //special case for boolean fields, as ESRI doesn't actually have them - assume numeric
        // it seems like we'll never encounter this code in practice, because ESRI fields don't translate to check boxes
        // but we should be including records when the field name starts with the search text and the field value is "true" or -1
        // in esri speak; searching for "true" is not really an intuitive approach
        if (
          value === true ||
          String(field.get("name"))
            .toLowerCase()
            .indexOf(String(value).toLowerCase()) === 0
        ) {
          return field.get("name") + " = -1";
        } else if (value === false) {
          return field.get("name") + " = 0";
        }
        {
          return "";
        }
      } else if (esriType != "esriFieldTypeDate" && !isNaN(value)) {
        //numeric fields fall here. they don't support the "LIKE" operation
        return field.get("name") + " = " + value;
      } else {
        return ""; //this is basically the case for date types, and we're not quite sure how to handle them right now.
      }
    }

    var externalFields = QueryModel.getQueryableFields({ layers: [layer] });

    var stringExpression = _.map(this.expression, function (andExpression) {
      var stringAndExpression = _.map(andExpression, function (clause) {
        if (clause.field) {
          return format(clause.field, clause.value);
        } else {
          return (
            "(" +
            _.compact(
              _.map(externalFields, function (field) {
                return format(field, clause.value);
              })
            ).join(" OR ") +
            ")"
          );
        }
      });

      return "(" + _.compact(stringAndExpression).join(" AND ") + ")";
    });

    return _.compact(stringExpression).join(" OR ");
  },

  /**
   * Convert the current contents of this `QueryModel` to an expression which can be used to filter map data models.
   * @return {Function} A function capable of filtering models by the constraints contained in this model.
   */
  toFilterFunction: function () {
    var self = this;

    function contains(field, model, value, likeLabeledFields) {
      var label = field.get("label");
      var modelValue;

      if (_.isObject(likeLabeledFields) && likeLabeledFields[label]) {
        //We are searching multiple layers
        var correctFieldId = _.find(likeLabeledFields[label], function (
          fieldId
        ) {
          return Field.getValue(model, fieldId);
        });

        if (correctFieldId) {
          modelValue = Field.getValue(model, correctFieldId);
        }
      } else {
        // We are only searching one Layer
        modelValue = field.getValue(model);
      }

      var result = false;

      if (field.get("type") == FieldTypes.CheckBox) {
        if (
          String(label).toLowerCase().indexOf(String(value).toLowerCase()) ===
            0 &&
          modelValue
        ) {
          result = modelValue;
        } else {
          result = modelValue === value;
        }
      } else if (field.get("type") == FieldTypes.Date) {
        //Check if both search value and model value are valid dates.
        if (Date.parse(modelValue) && Date.parse(value)) {
          var modelDate = new Date(modelValue);
          var searchDate = new Date(value);

          /*
           * Difference explanation: modelDate.getUTCDate() versus searchDate.getDate().
           * searchDate was just entered by user; we can read at face value.
           * ModelDate comes from the database as a UTC date string plus the currentTimeOfDay that the query happened.
           * The time of day of query is superfluous, so we only care about UTC date //todo remove current time of day of query
           */
          result =
            modelDate.getYear() == searchDate.getYear() &&
            modelDate.getMonth() == searchDate.getMonth() &&
            modelDate.getUTCDate() == searchDate.getDate();
        } else {
          result = false;
        }
      } else {
        result =
          String(modelValue)
            .toLowerCase()
            .indexOf(String(value).toLowerCase()) != -1;
      }

      return result;
    }

    return function filterModel(model) {
      var orResult = false,
        andResult;

      _.each(self.expression, function (andExpression) {
        andResult = true;

        _.each(andExpression, function (clause) {
          if (clause.field) {
            andResult =
              andResult && contains(clause.field, model, clause.value);
          } else {
            andResult =
              andResult &&
              _.any(self.fields, function (field) {
                return contains(field, model, clause.value);
              });
          }
        });

        orResult = orResult || andResult;
      });

      return orResult;
    };
  },
};

/**
 * Given the provided map data models or layer models, returns the layers associated with the search function.
 * @param {Object} options The hash of options.
 * @param {Array} [options.models] The set of models from which to extract the query fields. This or `options.layers` is required.
 * @param {Array} [options.layers] The set of layers from which to extract the query fields. This or `options.models` is required.
 * @returns {Array} The list of layers.
 */
QueryModel.getLayers = function (options, cb) {
  var layers = {};
  /* layerId -> layer*/

  if (options.models) {
    _.each(options.models, function (model) {
      var layer = model.originalLayer;

      if (layer) {
        var key = layer.name;

        if (layer.featureLayer || layer.info) {
          key = (layer.featureLayer || layer.info).id;
        }

        layers[key] = layer;
      }
    });

    layers = _.values(layers); //don't really need keys anymore
  } else {
    layers = options.layers;
  }

  return cb(layers);
};

/**
 * Given the provided map data models or layer models, returns a list of fields associated with all of the given models or layers.
 * @static
 * @param {Object} options The hash of options.
 * @param {Array} [options.models] The set of models from which to extract the query fields. This or `options.layers` is required.
 * @param {Array} [options.layers] The set of layers from which to extract the query fields. This or `options.models` is required.
 * @returns {Array} The list of fields, limited to those fields which are filterable, unique and sorted by name.
 */
QueryModel.getQueryableFields = function (options) {
  var layers = {};
  /* layerId -> layer*/
  var result;

  if (options.models) {
    _.each(options.models, function (model) {
      if (model) {
        var layer = model.originalLayer;

        if (layer) {
          var key = layer.name;

          if (layer.featureLayer || layer.info) {
            key = (layer.featureLayer || layer.info).id;
          }

          layers[key] = layer;
        }
      }
    });

    layers = _.values(layers); //don't really need keys anymore
  } else {
    layers = options.layers;
  }

  //Remove the null layers from the layers collection
  layers = _.filter(layers, function (layer) {
    if (layer != null && layer != undefined)
      if (layer._fields != null && layer._fields != undefined) return true;
    return false;
  });

  result = [];
  var uniqueFields = new Map();

  _.each(_.pluck(layers, "fields"), function (fields) {
    _.each(fields, function (field) {
      if (!uniqueFields.has(field.id)) {
        result.push(field);
        uniqueFields.set(field.id, "");
      }
    });
  });

  result = _.filter(result, function (field) {
    return field.isFilterable() || field.get("type") == FieldTypes.CheckBox;
  });
  result = _.sortBy(result, function (field) {
    return field.get("name");
  });
  result = _.uniq(result, true, function (field) {
    return field.get("name");
  });

  return result;
};

/**
 * Given layers, returns an object that holds layers that share the label attribute.
 * @param {Array} layers The layers involved in the search Query.
 * @returns {Object} Result where { label: [layerId1, layerId2], label2: [layerId3, layerId4]};
 */
QueryModel.getLikeLabeledFields = function (layers) {
  var result = {};

  _.each(
    _.flatten(
      _.map(layers, function (layer) {
        return layer.fields;
      })
    ),
    function (field) {
      var label = field.get("label");

      if (!result[label]) {
        result[label] = [];
      }
      result[label].push(field.get("name"));
    }
  );

  result = _.mapObject(result, function (value, key) {
    if (value.length > 1) {
      return value;
    } else {
      return null;
    }
  });

  return result;
};

export default QueryModel;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/query/QueryModel.js
// module id = 358
// module chunks = 1